import React from 'react';
import {useTranslation} from 'react-i18next';
import {Spinner} from '../Spinner';
import {adLevelLabel, adgroupLevelLabel} from '../../utils/channelLevelLabels';
import {platformName} from '../agency/connections_v2/platformsText';
import {RAW, capitalizeFirst, numberText, percentText} from './attributionText';

// Los valores que no cruzan se cuentan por nivel; arriba de esto, la caja sería una lista de basura.
const UNMATCHED_SHOWN = 5;

/**
 * Qué cruza una regla en una cuenta (molde: `Coverage` de `business_units/accounts/GA4FiltersPreview.js`).
 *
 * Una tarjeta por nivel (grupo de anuncios y anuncio) con el porcentaje de las sesiones que traen el
 * parámetro y coinciden con una entidad de la cuenta; debajo, los valores que no cruzan y por qué (sin
 * el parámetro, un nombre que no es de la cuenta, un grupo archivado, un nombre repetido dentro de una
 * campaña), y los nombres que se repiten entre campañas, que NO son un problema: se cruzan dentro de
 * cada una.
 *
 * Nunca bloquea el guardado: si GA4 no responde, lo dice en una línea.
 */
export function Ga4AttributionCoverage({channel, data, loading = false, errorCode = null, onRetry}) {
    const {t, i18n} = useTranslation();

    const header = (
        <div className="ga4-attr-coverage__head">
            <span className="ga4-attr-coverage__title">{t('ga4_attribution.preview.title')}</span>
            {loading && <Spinner color="positive small"/>}
        </div>
    );

    if (!data && loading) {
        return (
            <div className="ga4-attr-coverage -loading" aria-live="polite" aria-busy="true"
                 data-testid="ga4-attr-coverage">
                {header}
                <p className="ga4-attr-coverage__note">{t('ga4_attribution.preview.loading')}</p>
            </div>
        );
    }

    if (!data && errorCode) {
        return (
            <div className="ga4-attr-coverage -error" role="status" data-testid="ga4-attr-coverage">
                {header}
                <p className="ga4-attr-coverage__note">
                    {t('ga4_attribution.preview.error')}{' '}
                    {onRetry && (
                        <button type="button" className="ga4-attr-coverage__retry" onClick={onRetry}>
                            {t('ga4_attribution.agency.load_error_retry')}
                        </button>
                    )}
                </p>
            </div>
        );
    }

    if (!data) return null;

    const levels = [
        {key: 'adgroup', block: data.adgroup, label: (count) => adgroupLevelLabel(t, channel, {count})},
        {key: 'ad', block: data.ad, label: (count) => adLevelLabel(t, {count})},
    ];
    // Un nivel que se cruza solo (Google, por la vinculación con Google Ads) llega con `param: null`:
    // lo que identifica la sesión es la plataforma, no un UTM.
    const paramOf = (block) => block.param || platformName(channel);
    const unmatched = levels.flatMap(({key, block, label}) => (block && Array.isArray(block.unmatched)
        ? block.unmatched.slice(0, UNMATCHED_SHOWN).map((item) => ({...item, levelKey: key, param: paramOf(block), label}))
        : []));
    const duplicates = levels.flatMap(({key, block}) => (block && Array.isArray(block.duplicates)
        ? block.duplicates.map((item) => ({...item, levelKey: key}))
        : []));

    return (
        <div className={`ga4-attr-coverage${loading ? ' -stale' : ''}`} aria-live="polite" aria-busy={loading}
             data-testid="ga4-attr-coverage">
            {header}
            <div className="ga4-attr-coverage__cards">
                {levels.map(({key, block, label}) => (
                    <LevelCard key={key} t={t} language={i18n.language} block={block} label={label}
                               param={block ? paramOf(block) : null}/>
                ))}
            </div>
            {unmatched.length > 0 && (
                <div className="ga4-attr-coverage__list" data-testid="ga4-attr-unmatched">
                    <p className="ga4-attr-coverage__subtitle">{t('ga4_attribution.preview.unmatched_title')}</p>
                    <ul>
                        {unmatched.map((item, index) => (
                            <li key={`${item.levelKey}-${index}`} className="ga4-attr-coverage__item">
                                <code className="ga4-attr-coverage__value">{String(item.value)}</code>
                                <span className="ga4-attr-coverage__why">
                                    {/* `count` elige el plural; `sessions` es el número con el formato de la
                                        pantalla («1.240», no «1240»). */}
                                    {t('ga4_attribution.preview.unmatched_sessions', {
                                        count: Number(item.sessions) || 0,
                                        sessions: numberText(item.sessions, i18n.language),
                                    })}
                                    {' · '}
                                    {reasonText(t, item)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {duplicates.length > 0 && (
                <div className="ga4-attr-coverage__list" data-testid="ga4-attr-duplicates">
                    <p className="ga4-attr-coverage__subtitle">{t('ga4_attribution.preview.duplicates_title')}</p>
                    <ul>
                        {duplicates.map((item, index) => (
                            <li key={`${item.levelKey}-${index}`} className="ga4-attr-coverage__item">
                                {t('ga4_attribution.preview.duplicates_item', {
                                    name: item.name,
                                    count: Array.isArray(item.entities) ? item.entities.length : Number(item.entities) || 0,
                                    interpolation: RAW,
                                })}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/** Sin bloque, el nivel no se cruza; con bloque, se cruza por `param` (o solo, si es Google). */
function LevelCard({t, language, block, label, param}) {
    const title = capitalizeFirst(label(2));
    if (!block) {
        return (
            <div className="ga4-attr-coverage__card -off">
                <span className="ga4-attr-coverage__card-title">{title}</span>
                <span className="ga4-attr-coverage__note">{t('ga4_attribution.preview.no_param')}</span>
            </div>
        );
    }
    const matched = Number(block.matched_entities) || 0;
    return (
        <div className="ga4-attr-coverage__card">
            <span className="ga4-attr-coverage__card-title">{title}</span>
            <span className="ga4-attr-coverage__pct">{percentText(block.matched_pct, language) || '—'}</span>
            <span className="ga4-attr-coverage__card-text">
                {t('ga4_attribution.preview.card_pct', {
                    param, count: matched, levelPlural: label(matched), interpolation: RAW,
                })}
            </span>
        </div>
    );
}

/** Por qué un valor no cruza, con el rótulo de SU nivel («no es un conjunto de anuncios de esta cuenta»). */
function reasonText(t, item) {
    const reason = ['untagged', 'no_entity', 'archived', 'ambiguous', 'other_campaign'].includes(item.reason)
        ? item.reason : 'no_entity';
    return t(`ga4_attribution.preview.reason.${reason}`, {
        param: item.param, level: item.label(1), levelPlural: item.label(2), interpolation: RAW,
    });
}

export default Ga4AttributionCoverage;
